<template>
  <div
    :class="[
      'activity-item',
      `activity-item--${type}`,
      {
        'activity-item--reverse': isReversed,
        'activity-item--disabled': disabled,
      },
    ]"
  >
    <div
      class="activity-item__container"
      :style="{ animationDelay: `${index * 0.06}s` }"
      @click="$emit('select')"
    >
      <div class="activity-item__index">{{ index }}</div>
      <div :class="{ faded: disabled }">
        <ProgressStar v-if="lingos == 0" :progress="progress" />
        <ActivityLingos v-else :lingos="lingos" />
      </div>
      <img
        v-if="disabled"
        class="activity-item__padlock"
        src="/images/training-menu/padlock-green.webp"
      />
      <img
        :src="thumbnail"
        class="activity-item__thumb"
        alt="Activity background"
      />
    </div>

    <div v-if="showArrow" class="activity-item__arrow">
      <!-- eslint-disable -->
      <div v-for="(dot, ix) in dotsCount"
           :class="['activity-item__arrow-dot', {'empty-dot': dotEmpty(dot)}]"
           :style="{ animationDelay: `${(index * 0.06) + (ix * 0.1)}s` }"
      />
      <!-- eslint-enable -->

      <img
        v-if="dotEmpty(dotsCount + 1)"
        src="/images/training-menu/arrow-green-empty.png"
        class="activity-item__arrow-end"
        :style="{ animationDelay: `${index * 0.06 + (dotsCount + 1) * 0.1}s` }"
      />
      <img
        v-else
        src="/images/training-menu/arrow-green.png"
        class="activity-item__arrow-end"
        :style="{ animationDelay: `${index * 0.06 + (dotsCount + 1) * 0.1}s` }"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator';

import { ActivityItemTypes, StarTypes } from './models/enums';

import ProgressStar from './ProgressStar.vue';
import ActivityLingos from '@/screens/TrainingMenu/ActivityLingos.vue';

@Component({
  components: {
    ActivityLingos,
    ProgressStar,
  },
})
export default class ActivityItem extends Vue {
  @Prop({ type: Number, required: true })
  public index!: number;

  // Progress (percentage)
  @Prop({ type: Number, required: true })
  public progress!: number;

  @Prop({ type: String, default: ActivityItemTypes.Primary })
  public type!: ActivityItemTypes;

  @Prop({ type: String, required: true })
  public thumbnail!: string;

  @Prop({ type: Boolean, required: true })
  public disabled!: boolean;

  @Prop({ type: Boolean, default: true })
  public showArrow!: boolean;

  @Prop({ type: Number, required: true })
  public lingos!: number;

  // Score threshold (percentage)
  @Prop({ type: Number, required: true })
  public scoreThreshold!: number;

  public get dotsCount() {
    return this.windowWidth > 765 ? 6 : 3;
  }

  public dotEmpty(dot: number) {
    // Include the arrow in the dot count
    return dot / (this.dotsCount + 1) > this.progress / this.scoreThreshold;
  }

  public mounted() {
    const observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('entrance');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '0px',
        threshold: 0,
      },
    );
    document
      .querySelectorAll('.activity-item__container')
      .forEach(el => observer.observe(el));
    document
      .querySelectorAll('.activity-item__arrow-dot')
      .forEach(el => observer.observe(el));
    document
      .querySelectorAll('.activity-item__arrow-end')
      .forEach(el => observer.observe(el));
  }

  public get starType() {
    if (this.progress === 100) {
      return StarTypes.Full;
    }

    if (this.progress >= 50) {
      return StarTypes.Half;
    }

    return StarTypes.Empty;
  }

  public get isReversed() {
    return this.index % 2 === 0;
  }
}
</script>

<style lang="scss" scoped>
@mixin coloured-wrapper($color, $shadowColor) {
  border-radius: 100%;
  background-color: $color;
  border: 2px solid $shadowColor;
}

@mixin coloured-wrapper-with-shadow($color, $shadowColor) {
  @include coloured-wrapper($color, $shadowColor);

  box-shadow: 1px 2px 2px $shadowColor;
}

.faded {
  opacity: 0.85;
}

.entrance {
  animation: expanding 0.4s ease-out forwards;
  transform: scale(0);
}

@keyframes expanding {
  from {
    transform: scale(0);
  }
  85% {
    transform: scale(1.1);
  }
  to {
    transform: scale(1);
  }
}

.activity-item {
  @include d-flex-items-end;

  @media (max-width: #{$mobile}px) {
    align-items: flex-start;
  }

  &__thumb {
    border-radius: 100%;
    width: 100%;
    height: 100%;
  }

  &__padlock {
    position: absolute;
    background-color: white;
    border-radius: 50%;
    border-style: solid;
    border-width: 3px;
    border-color: #88c23f;
    width: 80px;
    top: 64px;
    left: 66px;

    @media (max-width: #{$mobile}px) {
      width: 50px;
      top: 40px;
      left: 42px;
    }
  }

  &__container {
    transform: scale(0);
    position: relative;
    cursor: pointer;

    width: 210px;
    height: 210px;

    @include coloured-wrapper-with-shadow($blue400, $blue100);

    padding: 20px;

    @media (max-width: #{$mobile}px) {
      width: 135px;
      height: 135px;

      padding: 15px;
    }
  }

  &--danger {
    .activity-item__container {
      @include coloured-wrapper-with-shadow($pink200, $pink200Shadow);
    }
  }

  &--disabled {
    pointer-events: none;
  }

  &__index {
    @include d-flex-center;
    @include coloured-wrapper-with-shadow($blue400, $blue100);

    width: 75px;
    height: 75px;

    color: $white100;
    font-weight: bold;
    font-size: 45px;

    position: absolute;
    top: -30px;
    left: -15px;

    @media (max-width: #{$mobile}px) {
      width: 50px;
      height: 50px;

      font-size: 30px;
    }
  }

  .progress-star {
    position: absolute;
    bottom: 10px;
    right: -20px;

    @media (max-width: #{$mobile}px) {
      width: 65px;
      height: 65px;
    }
  }

  .empty-dot {
    background-color: transparent;
    border-style: solid;
    border-width: 3px;
    border-color: $green400;
  }

  &__arrow {
    height: calc(100% + 25px);
    flex: 1;

    height: 135px;
    display: flex;

    margin-left: -10px;

    @media (max-width: #{$mobile}px) {
      margin-top: 50px;
      height: auto;
    }

    &-dot {
      transform: scale(0);
      border-radius: 100%;

      width: 25px;
      height: 25px;

      background-color: $green400;

      &:nth-child(1),
      &:nth-child(2),
      &:nth-child(3) {
        margin-right: 25px;
      }

      &:nth-child(1) {
        @media (max-width: #{$mobile}px) {
          margin-right: 10px;
        }
      }

      &:nth-child(2) {
        @media (max-width: #{$mobile}px) {
          margin-top: 30px;
          margin-right: 0;
        }
      }

      &:nth-child(3) {
        @media (max-width: #{$mobile}px) {
          margin-top: 75px;
          margin-left: -10px;
        }
      }

      &:nth-child(4) {
        margin-right: 10px;
        margin-top: 12px;

        @media (max-width: #{$mobile}px) {
          margin: 0;
        }
      }

      &:nth-child(5) {
        margin-top: 37px;
      }

      &:nth-child(6) {
        margin-top: 75px;
      }
    }

    &-end {
      height: 40px;

      margin-top: 125px;
      margin-left: -45px;

      @media (max-width: #{$mobile}px) {
        height: 20px;
        margin-top: 130px;
        margin-left: -45px;
      }
    }
  }

  &--reverse {
    flex-direction: row-reverse;

    .activity-item__arrow {
      flex-direction: row-reverse;
      margin-left: 0;
      margin-right: -10px;

      &-end {
        margin-left: 0;
        margin-right: -45px;
      }

      &-dot {
        margin-right: 0;

        &:nth-child(1),
        &:nth-child(2),
        &:nth-child(3) {
          margin-left: 25px;
        }

        &:nth-child(1) {
          @media (max-width: #{$mobile}px) {
            margin-left: 10px;
          }
        }

        &:nth-child(2) {
          @media (max-width: #{$mobile}px) {
            margin-top: 30px;
            margin-left: 0;
          }
        }

        &:nth-child(3) {
          @media (max-width: #{$mobile}px) {
            margin-top: 75px;
            margin-right: -10px;
          }
        }

        &:nth-child(4) {
          margin-left: 10px;

          @media (max-width: #{$mobile}px) {
            margin: 0;
          }
        }
      }
    }
  }
}
</style>
